import * as dialogs from "@client/features/dialogs";
import * as journal from "@client/features/journal";
import {removeEntryFromDay, updateEntryInDay} from "@client/features/journal";
import {notify} from "@client/features/notifications";
import * as orm from "@client/features/orm";
import {showFeelerIfFirstTime} from "@client/features/scenes";
import {fetchSearch, loadSearch} from "@client/features/search";
import {
    Capacitor,
    type DepthMap,
    getAppInfo,
    openSettings,
    takePicture,
} from "@client/hardware";
import * as journeys from "@client/journeys";
import * as analytics from "@client/shared/analytics";
import {Spinner} from "@client/site/components";
import {useAppDispatch} from "@client/store";
import * as utils from "@client/utils";
import {Schema, server} from "@reactivated";
import React from "react";
import {P, match} from "ts-pattern";

type NutritionalVisionPrompt = Schema["rpc_queue_meal_image_output"];
export type ExpandedEntry = server.journal.nutrition.ExpandedEntry.output;
export type PromptType = NonNullable<
    ExpandedEntry["nutritional_vision_prompt"]
>["type"];

export type ResolvedEntry =
    | {kind: "processing"; uuid: string; type: PromptType}
    | {
          kind: "identified";
          uuid: string;
          name: string;
          type: PromptType;
          imageUid: string | null;
      }
    | {
          kind: "complete";
          uuid: string;
          name: string;
          entry: ExpandedEntry;
          imageUid: string | null;
      }
    | {kind: "error"; uuid: string; name: string | null; type: PromptType};

export function resolveEntry(entry: ExpandedEntry): ResolvedEntry {
    const prompt = entry.nutritional_vision_prompt;

    if (prompt == null) {
        return {
            kind: "complete",
            uuid: entry.uuid,
            name: entry.name,
            entry,
            imageUid: null,
        };
    }

    const imageUid = prompt.image?.image_uid ?? null;

    // IDENTIFIED with a name → "identified"; IDENTIFIED without → falls through to "processing"
    return match(prompt)
        .with({status: "IDENTIFIED", name: P.string}, (p) => ({
            kind: "identified" as const,
            uuid: entry.uuid,
            name: p.name,
            type: p.type,
            imageUid,
        }))
        .with({status: P.union("PROCESSING", "IDENTIFIED")}, () => ({
            kind: "processing" as const,
            uuid: entry.uuid,
            type: prompt.type,
        }))
        .with({status: "SUCCESS"}, () => ({
            kind: "complete" as const,
            uuid: entry.uuid,
            name: entry.name,
            entry,
            imageUid,
        }))
        .with({status: P.union("ERROR", "INVALID")}, (p) => ({
            kind: "error" as const,
            uuid: entry.uuid,
            name: p.name,
            type: p.type,
        }))
        .exhaustive();
}

export type CaptureAndUploadResult =
    | {
          status: "success";
          type: "meal" | "facts";
          prompt: NutritionalVisionPrompt;
      }
    | {
          status: "success";
          type: "barcode";
          prompt: NutritionalVisionPrompt;
          barcode: string;
      }
    | {status: "canceled"}
    | {status: "no_permission"}
    | {status: "search"; type: "meal" | "barcode" | "facts"}
    | {status: "error"; error: string};

export type CaptureResult =
    | {type: "meal_image"; prompt: NutritionalVisionPrompt}
    | {type: "facts_image"; prompt: NutritionalVisionPrompt}
    | {type: "barcode"; barcode: string; prompt: NutritionalVisionPrompt}
    | {
          type: "entries";
          entries: ExpandedEntry[];
          meal: server.journal.nutrition.StagedMeal.output;
          method: "now" | "cupboard";
      }
    | {type: "canceled"};

async function getFileFromInput(fileInputId: string): Promise<File | null> {
    return new Promise((resolve) => {
        const input = document.getElementById(fileInputId) as HTMLInputElement | null;
        if (input == null) {
            resolve(null);
            return;
        }

        const controller = new AbortController();

        input.addEventListener(
            "change",
            () => {
                const file = input.files?.[0] ?? null;
                controller.abort();
                // Reset value so the same file can be selected again
                input.value = "";
                resolve(file);
            },
            {signal: controller.signal},
        );

        input.addEventListener(
            "cancel",
            () => {
                controller.abort();
                resolve(null);
            },
            {signal: controller.signal},
        );

        input.click();
    });
}

export async function captureAndUploadImage(options: {
    fileInputId: string;
    setEncodedImage: (encoded: string) => void;
    mode?: "meal" | "barcode" | "facts" | "barcode_only";
}): Promise<CaptureAndUploadResult> {
    const {fileInputId, setEncodedImage} = options;
    const barcodeOnly = options.mode === "barcode_only";

    // Start fetching upload URL immediately (don't await yet)
    const uploadURLPromise = server.core.rpc.create_user_media_image({
        ignored: "",
    });

    // Try native camera first
    const nativeResult = await takePicture({useDepthCamera: true, mode: options.mode});

    type Capture =
        | {type: "barcode"; barcode: string; depthMap: null}
        | {type: "meal" | "facts"; depthMap: DepthMap | null};

    let file: File;
    let capture: Capture;

    if (nativeResult.status === "success") {
        const picture = nativeResult.data;

        if (picture.status !== "success") {
            if (picture.status === "canceled") {
                return {status: "canceled"};
            }
            if (picture.status === "no_permission") {
                return {status: "no_permission"};
            }
            if (picture.status === "search") {
                return {status: "search", type: picture.type};
            }
            return {status: "error", error: picture.error};
        }

        if (picture.type === "barcode") {
            // Filter out QR codes and other non-UPC/EAN formats for
            // backwards compatibility with older native builds.
            if (!/^\d{6,13}$/.test(picture.barcode)) {
                return {
                    status: "error",
                    error: "QR codes are not supported.\nPlease scan a product barcode.",
                };
            }
            capture = {type: "barcode", barcode: picture.barcode, depthMap: null};
        } else {
            // Guard against old native builds that ignore barcode_only mode
            // and allow meal/facts captures.
            if (barcodeOnly) {
                return {
                    status: "error",
                    error: "Meal photos are not supported here.\nPlease update your app or use barcode scanning.",
                };
            }
            capture = {
                type: picture.type,
                depthMap: picture.depth_map ?? null,
            };
        }

        // Convert native file path to File object
        const webViewPath = Capacitor.convertFileSrc(picture.path);
        const response = await fetch(webViewPath);
        const blob = await response.blob();
        file = new File([blob], "camera-photo.jpg", {type: "image/jpeg"});
    } else {
        // Fall back to file input
        const selectedFile = await getFileFromInput(fileInputId);

        if (selectedFile == null) {
            return {status: "canceled"};
        }

        file = selectedFile;
        capture = {type: "meal", depthMap: null};
    }

    // Read as data URL for preview
    const reader = new FileReader();
    reader.readAsDataURL(file);
    const encoded = await new Promise<string>((resolve, reject) => {
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = () => reject(new Error("Failed to read file"));
    });
    setEncodedImage(encoded);

    // Await the upload URL we started fetching earlier
    const uploadURLResult = await uploadURLPromise;

    if (uploadURLResult.type !== "success") {
        return {status: "error", error: "Error getting upload image URL"};
    }
    const {image_uid, upload_url} = uploadURLResult.data;

    // Resize and upload
    const uploadForm = new FormData();
    const resizedImage = await utils.resizeImage(file, 1024);

    if (resizedImage == null) {
        return {status: "error", error: "Error resizing image"};
    }
    uploadForm.append("file", resizedImage, file.name);

    await (await fetch(upload_url, {body: uploadForm, method: "POST"})).json();

    void analytics.track(
        capture.type === "barcode"
            ? "UPLOADED_BARCODE_IMAGE"
            : capture.type === "facts"
              ? "UPLOADED_NUTRITION_FACTS_IMAGE"
              : "UPLOADED_NUTRITIONAL_IMAGE",
    );

    const queueResult =
        capture.type === "barcode"
            ? await server.journal.nutritional_ai.queue_barcode({
                  image_uid,
                  filename: file.name,
                  barcode: capture.barcode,
                  depth_map: capture.depthMap,
              })
            : await server.journal.nutritional_ai.queue_meal_image({
                  image_uid,
                  filename: file.name,
                  description: "",
                  depth_map: capture.depthMap,
              });

    if (queueResult.type !== "success") {
        return {
            status: "error",
            error:
                capture.type === "barcode"
                    ? "Error queuing barcode"
                    : "Error queuing meal image",
        };
    }

    if (capture.type === "barcode") {
        return {
            status: "success",
            type: capture.type,
            prompt: queueResult.data,
            barcode: capture.barcode,
        };
    }

    return {
        status: "success",
        type: capture.type,
        prompt: queueResult.data,
    };
}

export type ProcessResult =
    | {kind: "complete"; entry: ExpandedEntry}
    | {kind: "error"; message: string};

export function createPlaceholderEntry(
    uuid: string,
    prompt: NutritionalVisionPrompt,
): ExpandedEntry {
    return {
        uuid: uuid as UUID,
        unit: "serving",
        quantity: "1",
        units: [
            {
                unit: "serving",
                multiplier: "1",
                calories: null,
                info: "serving size unit not same as base unit",
            },
        ],
        // A placeholder until the analysis names it; the server refuses a blank name.
        name: "Analyzing…",
        serving_size_unit: "serving",
        serving_size_numerator: "1",
        serving_size_denominator: "1",
        container_unit: "",
        container_quantity: null,
        container_servings: "1",
        base_unit: "",
        base_quantity: null,
        nutritional_vision_prompt: prompt,
        icon: null,
        calories_pg: null,
        protein_pg: null,
        fat_pg: null,
        carbs_pg: null,
        fiber_pg: null,
        net_carbs_pg: null,
        sodium_pg: null,
        saturated_fat_pg: null,
        monounsaturated_fat_pg: null,
        polyunsaturated_fat_pg: null,
        trans_fat_pg: null,
        cholesterol_pg: null,
        sugars_pg: null,
        added_sugars_pg: null,
        potassium_pg: null,
        magnesium_pg: null,
        vitamin_a_dv_pg: null,
        vitamin_c_dv_pg: null,
        calcium_dv_pg: null,
        iron_dv_pg: null,
        usda_food_id: null,
        backend: "inline",
        backend_id: "",
        expanded_recipe: [],
        additional_units: [],
        notes: "",
    };
}

export async function processEntry(
    entryUUID: string,
    prompt: NutritionalVisionPrompt,
): Promise<ProcessResult> {
    const rpcCall = match(prompt.type)
        .with("BARCODE", () =>
            server.journal.nutritional_ai.process_queued_barcode({
                form: entryUUID as UUID,
            }),
        )
        .with("AUDIO", () =>
            server.journal.nutritional_ai.process_queued_audio({
                form: entryUUID as UUID,
            }),
        )
        .with("VISION", () =>
            server.journal.nutritional_ai.process_queued_image({
                form: entryUUID as UUID,
            }),
        )
        .otherwise(() => {
            throw new Error(`Unsupported prompt type: ${prompt.type}`);
        });

    const result = await rpcCall;

    if (result.type !== "success") {
        return {kind: "error", message: "Failed to process entry. Please try again."};
    }

    const entry = result.data;
    const status = entry.nutritional_vision_prompt?.status;

    if (status === "ERROR" || status === "INVALID") {
        const message = match(prompt.type)
            .with(
                "BARCODE",
                () =>
                    "Barcode not found in our database.\nTry searching for it manually.",
            )
            .with(
                "AUDIO",
                () => "Failed to analyze audio.\nPlease try again or log manually.",
            )
            .with(
                "VISION",
                () => "Failed to analyze image.\nPlease try again or log manually.",
            )
            .otherwise(() => "Processing failed.");

        return {kind: "error", message};
    }

    return {kind: "complete", entry};
}

type Journal = server.journal.day.Journal.output;

type SetJournal = (updater: (prev: Journal) => Journal) => void;

export async function processAndUpdateEntry(options: {
    entryUUID: string;
    prompt: NutritionalVisionPrompt;
    setJournal: SetJournal;
    notifyError: (message: string) => void;
}): Promise<ProcessResult> {
    const {entryUUID, prompt, setJournal, notifyError} = options;

    // Fire-and-forget: show meal name as soon as it's identified
    if (prompt.type === "VISION") {
        void server.journal.nutritional_ai
            .identify_meal_name({form: prompt.uuid})
            .then((r) => {
                if (r.type === "success") {
                    setJournal((prev) => ({
                        ...prev,
                        day: updateEntryInDay(prev.day, entryUUID, (entry) => {
                            // Don't overwrite if processing already completed
                            if (entry.nutritional_vision_prompt?.status === "SUCCESS") {
                                return entry;
                            }
                            return {
                                ...entry,
                                nutritional_vision_prompt: r.data,
                            };
                        }),
                    }));
                }
            });
    }

    const result = await processEntry(entryUUID, prompt);

    if (result.kind === "error") {
        void server.journal.nutrition.delete_entry({form: entryUUID as UUID});
        notifyError(result.message);
        setJournal((prev) => ({
            ...prev,
            day: removeEntryFromDay(prev.day, entryUUID),
        }));
        return result;
    }

    setJournal((prev) => ({
        ...prev,
        day: updateEntryInDay(prev.day, entryUUID, () => result.entry),
    }));
    return result;
}

interface UseCaptureOptions {
    target:
        | {type: "DAY"; day: server.journal.nutrition.Day.output}
        | {type: "RECIPE"; recipe: server.journal.nutrition.Recipe.output}
        | null;
    profile: server.core.rpc.Profile.output;
    cameraEnabled?: boolean;
}

interface HandlePhotoOptions {
    currentJournal: Journal;
    setJournal: SetJournal;
    onProcessed?: (info: {
        result: ProcessResult;
        captureType: "meal" | "barcode" | "facts";
    }) => void | Promise<void>;
}

interface CaptureStartOptions {
    initialView: "camera" | "manual";
    cameraMode?: "meal" | "barcode" | "barcode_only";
    preSelectedMealName?: string;
}

export function useCapture(options: UseCaptureOptions) {
    const {target, profile, cameraEnabled = true} = options;
    const dispatch = useAppDispatch();
    const [encodedImage, setEncodedImage] = React.useState<string | null>(null);

    const entryDialog = journeys.useEntryDialog();
    const barcodeEntryDialog = journeys.useEntryDialog();
    const fileInputId = "capture-file-input";

    const nutritionalVisionAnnouncement = dialogs.useDialog<null>({
        Content: (props) => {
            return (
                <div className="z-10 flex flex-col items-center justify-center space-y-6 bg-white p-4 text-center text-pretty">
                    <h1 className="text-3xl font-light">Tracking with pictures</h1>
                    <div className="flex flex-col space-y-[4dvh] text-xl text-pretty text-gray-800">
                        <p>Only capture the food you ate.</p>
                        <p>Avoid stacking food on top of each other.</p>
                        <p>Make sure everything is visible.</p>
                        <button
                            type="button"
                            className="w-full rounded-full bg-sky-600 px-8 py-4 text-lg font-medium text-white"
                            onClick={() => props.close(null)}
                        >
                            Got it
                        </button>
                    </div>
                </div>
            );
        },
    });

    const handlePhoto = (
        prompt: NutritionalVisionPrompt,
        captureType: "meal" | "barcode" | "facts",
        photoOptions: HandlePhotoOptions & {preSelectedMealName?: string},
    ) => {
        const {currentJournal, setJournal, onProcessed, preSelectedMealName} =
            photoOptions;
        const entryUUID = crypto.randomUUID();

        const {updatedJournal} = journal.addEntriesToJournal({
            journal: currentJournal,
            meal:
                preSelectedMealName != null
                    ? {name: preSelectedMealName, type: "MANUAL", hour: null}
                    : journal.autoStagedMeal(new Date(), currentJournal.day.key),
            dispatch,
            preserveUUIDs: true,
            journal_mode:
                captureType === "barcode"
                    ? "BARCODE"
                    : captureType === "facts"
                      ? "FACTS"
                      : "VISION",
            entries: [createPlaceholderEntry(entryUUID, prompt)],
            updated: async (updatedStreak) => {
                document.getElementById(entryUUID)?.scrollIntoView({
                    behavior: "smooth",
                });

                setJournal((prev) => ({
                    ...prev,
                    streak: updatedStreak,
                }));

                const result = await processAndUpdateEntry({
                    entryUUID,
                    prompt,
                    setJournal,
                    notifyError: (msg) => dispatch(notify(msg, {type: "error"})),
                });

                if (result.kind === "complete") {
                    dispatch(showFeelerIfFirstTime());
                    const analyticsEvent =
                        captureType === "barcode"
                            ? "PROCESSED_BARCODE"
                            : captureType === "facts"
                              ? "ANALYZED_NUTRITION_FACTS"
                              : "ANALYZED_NUTRITIONAL_IMAGE";
                    void analytics.track(analyticsEvent);
                }
                await onProcessed?.({result, captureType});
            },
        });
        setEncodedImage(null);
        setJournal(() => updatedJournal);
    };

    type CaptureStep =
        | {view: "camera"; mode: "meal" | "barcode" | "facts" | "barcode_only"}
        | {view: "manual"; highlightIcons: boolean}
        | {view: "done"; result: CaptureResult};

    const runCameraStep = async (
        preSelectedMealName: string | undefined,
        mode: "meal" | "barcode" | "facts" | "barcode_only",
    ): Promise<CaptureStep | null> => {
        if (window.__E2E_CAPTURE_RESULT__ != null) {
            const result = window.__E2E_CAPTURE_RESULT__ as CaptureResult;
            window.__E2E_CAPTURE_RESULT__ = undefined;
            return {view: "done", result};
        }

        const {platform} = await getAppInfo();
        if (platform === "web" && (mode === "barcode" || mode === "barcode_only")) {
            dispatch(
                notify("Barcode scanning is not available on the web version", {
                    type: "error",
                }),
            );
            return {view: "done", result: {type: "canceled"}};
        }

        if (
            !profile.user.casted_dismissed_announcements.includes(
                "NUTRITIONAL_VISION_INSTRUCTIONS",
            )
        ) {
            await nutritionalVisionAnnouncement();
            void dispatch(orm.dismissAnnouncement("NUTRITIONAL_VISION_INSTRUCTIONS"));
        }

        const uploadResult = await captureAndUploadImage({
            fileInputId,
            setEncodedImage,
            mode,
        });

        if (uploadResult.status === "search") {
            const highlightIcons =
                !profile.user.casted_dismissed_announcements.includes(
                    "CAMERA_SEARCH_TUTORIAL",
                );
            if (highlightIcons) {
                void dispatch(orm.dismissAnnouncement("CAMERA_SEARCH_TUTORIAL"));
            }
            return {view: "manual", highlightIcons};
        }

        if (uploadResult.status === "error") {
            dispatch(notify(uploadResult.error, {type: "error"}));
            return {view: "done", result: {type: "canceled"}};
        }

        if (uploadResult.status === "no_permission") {
            dispatch(
                notify("You need to grant permission to access the camera", {
                    type: "error",
                }),
            );
            void openSettings();
            return {view: "done", result: {type: "canceled"}};
        }

        if (uploadResult.status === "success") {
            setEncodedImage(null);
            const result = match(uploadResult)
                .with({type: "barcode"}, (r) => ({
                    type: "barcode" as const,
                    barcode: r.barcode,
                    prompt: r.prompt,
                }))
                .with({type: "facts"}, (r) => ({
                    type: "facts_image" as const,
                    prompt: r.prompt,
                }))
                .with({type: "meal"}, (r) => ({
                    type: "meal_image" as const,
                    prompt: r.prompt,
                }))
                .exhaustive();

            return {view: "done", result};
        }

        return {view: "done", result: {type: "canceled"}};
    };

    const runManualStep = async (
        preSelectedMealName: string | undefined,
        highlightIcons: boolean,
    ): Promise<CaptureStep | null> => {
        await dispatch(loadSearch());

        const result = await entryDialog({
            target,
            highlightIcons,
            preSelectedMealName,
            cameraEnabled,
        });

        if (result == null) {
            return {view: "done", result: {type: "canceled"}};
        }

        if (result.type === "switch_to_camera") {
            return {view: "camera", mode: result.captureType};
        }

        return {
            view: "done",
            result: {
                type: "entries",
                entries: result.entries,
                meal: result.meal,
                method: result.method,
            },
        };
    };

    const start = async (startOptions: CaptureStartOptions): Promise<CaptureResult> => {
        await analytics.requestTrackingAuthorization(profile, dispatch);
        const {platform} = await getAppInfo();

        let step: CaptureStep | null =
            startOptions.initialView === "camera" && platform !== "web"
                ? {view: "camera", mode: startOptions.cameraMode ?? "meal"}
                : {view: "manual", highlightIcons: false};

        while (step != null) {
            if (step.view === "done") {
                return step.result;
            }
            step =
                step.view === "camera"
                    ? await runCameraStep(startOptions.preSelectedMealName, step.mode)
                    : await runManualStep(
                          startOptions.preSelectedMealName,
                          step.highlightIcons,
                      );
        }

        return {type: "canceled"};
    };

    const dialog = (
        <>
            <input type="file" className="hidden" id={fileInputId} accept="image/*" />
            {nutritionalVisionAnnouncement.dialog}
            {entryDialog.dialog}
            {barcodeEntryDialog.dialog}
            {encodedImage != null && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
                    <div className="relative">
                        <img
                            className="max-w-64 rounded-lg"
                            src={encodedImage}
                            alt="Uploaded image"
                        />
                        <div className="absolute inset-0 flex items-center justify-center rounded-lg bg-black/30 text-white">
                            <Spinner />
                        </div>
                    </div>
                </div>
            )}
        </>
    );

    type BarcodeResult = {
        entries: ExpandedEntry[];
        meal: server.journal.nutrition.StagedMeal.output;
        method: "now" | "cupboard";
    };

    const resolveBarcode = async (barcode: string): Promise<BarcodeResult | null> => {
        const results = await dispatch(fetchSearch({upc: barcode}));
        if (results.length > 0) {
            const journeyResult = await barcodeEntryDialog({
                target,
                cameraEnabled: false,
                initial: {type: "entries", entries: [results[0].entry]},
                route: "barcode_found_confirm_entry.macros",
            });
            if (journeyResult?.type === "entries") {
                return journeyResult;
            }
        } else {
            dispatch({type: "NO_RESULTS"});
            const confirmed = await dispatch(
                dialogs.confirm({
                    message: "Product not found",
                    body: `No matches found for UPC ${barcode}.\n\nWould you like to create a new food with this UPC?`,
                }),
            );
            if (confirmed) {
                const journeyResult = await barcodeEntryDialog({
                    target,
                    cameraEnabled: false,
                    initial: {
                        type: "food_from_search",
                        upc: barcode,
                        quantity: null,
                        unit: null,
                    },
                    route: "barcode_not_found_create_food.macros",
                });
                if (journeyResult?.type === "entries") {
                    return journeyResult;
                }
            }
        }
        return null;
    };

    return {start, handlePhoto, resolveBarcode, dialog};
}
